import { parseJson } from '@/lib/common';
import React, { useCallback, useEffect, useState } from 'react';

export type UseLocalStorageStateType<V> = [
  V,
  React.Dispatch<React.SetStateAction<V>>,
];

const getCurrentLocalStorageValue = (storageKey: string, defaultValue: any) => {
  if (typeof window === 'undefined') return defaultValue;
  return parseJson(localStorage.getItem(storageKey), defaultValue);
};

export function useLocalStorageState<V>(
  defaultValue: V,
  storageKey: string,
): UseLocalStorageStateType<V> {
  // Always initialise with defaultValue so the server render and the client's
  // first (hydration) render produce identical HTML. Reading localStorage
  // synchronously here would make the client's initial render diverge from the
  // server's, causing a hydration mismatch. The stored value is applied right
  // after mount via the effect below.
  const [value, setValue] = useState<V>(defaultValue);

  const setStorageValue = useCallback(
    (storedValue: V | ((preValue: V) => V)) => {
      const valueToStore =
        storedValue instanceof Function ? storedValue(value as V) : storedValue;
      setValue(valueToStore);
      localStorage.setItem(storageKey, JSON.stringify(valueToStore));
    },
    [storageKey, value],
  );

  useEffect(() => {
    const currentValue = getCurrentLocalStorageValue(storageKey, defaultValue);
    setValue(currentValue);
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setStorageValue];
}
